import { hasAtLeastOneValidCondition } from './rule-conditions';
import { buildRuleFiredConditions, extractRuleFiredId } from './rule-fired';
import {
  CancelGuard,
  DelayActionsGuard,
  Flow,
  FlowGuardOperator,
  FlowGuard,
  FlowTimeCustomOperators,
  RateLimitGuard
} from 'qx-flow-orm';
import { defineAsyncComponent, Component } from 'vue';
import { RULE_FORM_ERRORS, RULE_GUARD_CANCEL_SCOPE_OPTIONS } from '@/config/rule';
import { asyncSome } from '@/functions/array';
import { ICON } from '@/constants/icons';
import { RuleCondition, RuleGuardOf } from '../types';
import { FAIcon } from '@/types';

export type GuardComponentType = {
  name: FlowGuardOperator, // only used by RuleGuardsAddGuard.vue
  icon: FAIcon;
  title: string;
  desc: string;
  form: FlowGuard;
  validate: (args: unknown) => Promise<boolean>;
  displayComponent: Component;
  formComponent: Component;
};

export type GuardComponentMapType = {
  [key in FlowGuardOperator]: GuardComponentType
};

type GuardDelayForm = FlowGuard & {
  options: {
    delay: number,
    conditions: RuleCondition[]
  }
}

type GuardOccurenceForm = FlowGuard & {
  options: {
    expires: {
      type: FlowTimeCustomOperators,
      value: number
    },
    value: number
  }
}

const cancelConditionsError = (conditions: RuleCondition[]) => {
  const ruleFiredId = extractRuleFiredId(conditions);
  if (ruleFiredId !== null) return ruleFiredId ? null : RULE_FORM_ERRORS.RULE_GUARD_CANCEL_MISSING_RULE;
  return hasAtLeastOneValidCondition(conditions) ? null : RULE_FORM_ERRORS.RULE_GUARD_CANCEL_MISSING_CONDITIONS;
};

export const guardComponentMap: GuardComponentMapType = {
  [FlowGuardOperator.RateLimit]: {
    name: FlowGuardOperator.RateLimit,
    icon: ['fas', ICON.GAUGE_HIGH],
    title: 'Rate Limit Guard',
    desc: 'Limit how often the rule fires, per person, camera, door...',
    form: {
      type: FlowGuardOperator.RateLimit,
      options: {
        seconds: 300,
        groupBy: 'personID',
        delay: 0
      }
    },
    validate: async (form: RuleGuardOf<RateLimitGuard>) => {
      if (!(Number(form.options.seconds) > 0)) throw RULE_FORM_ERRORS.RULE_GUARD_RATE_LIMIT_INVALID_SECONDS;
      if (!form.options.groupBy) throw RULE_FORM_ERRORS.RULE_GUARD_RATE_LIMIT_MISSING_GROUP_BY;
      if (form.options.delay != null && !(Number(form.options.delay) >= 0)) {
        throw RULE_FORM_ERRORS.RULE_GUARD_RATE_LIMIT_INVALID_DELAY;
      }
      return true;
    },
    displayComponent: defineAsyncComponent(() => import('@/models/rule/components/guards/RuleGuardRateLimit.vue')),
    formComponent: defineAsyncComponent(() => import('@/models/rule/components/guards/RuleGuardRateLimitForm.vue'))
  },
  [FlowGuardOperator.DelayActions]: {
    name: FlowGuardOperator.DelayActions,
    icon: ['fas', ICON.CLOCK],
    title: 'Delay Actions',
    desc: 'Run the actions after a delay',
    form: {
      type: FlowGuardOperator.DelayActions,
      options: {
        delay: 300
      }
    },
    validate: async (form: RuleGuardOf<DelayActionsGuard>) => {
      if (!(Number(form.options.delay) > 0)) throw RULE_FORM_ERRORS.RULE_ACTION_DELAY_INVALID;
      return true;
    },
    displayComponent: defineAsyncComponent(() => import('@/models/rule/components/guards/RuleGuardDelayActions.vue')),
    formComponent: defineAsyncComponent(() => import('@/models/rule/components/guards/RuleGuardDelayActionsForm.vue'))
  },
  [FlowGuardOperator.Occurrence]: {
    name: FlowGuardOperator.Occurrence,
    icon: ['fas', 'repeat-1-alt'],
    title: 'Repeat Guard',
    desc: 'Identify repetitious behavior',
    form: {
      type: FlowGuardOperator.Occurrence,
      options: {
        expires: {
          type: FlowTimeCustomOperators.SecondsAgo,
          value: 15
        },
        value: 3
      }
    },
    validate: async (form: GuardOccurenceForm) => {
      try {
        if (form.options.expires.type != FlowTimeCustomOperators.SecondsAgo) throw `Bad operator. Expecting ${FlowTimeCustomOperators.SecondsAgo}`;
        if (!(Number(form.options.expires.value) > 0)) throw "Time window must be a positive number"
        if (!(Number(form.options.value) > 0)) throw "Occurence count must be a positive number"
        return true
      } catch (error) {
        throw error
      }
    },
    displayComponent: defineAsyncComponent(() => import('@/models/rule/components/guards/RuleGuardRepeat.vue')),
    formComponent: defineAsyncComponent(() => import('@/models/rule/components/guards/RuleGuardRepeatForm.vue'))
  },
  // '_release': {
  //   name: '_release',
  //   icon: ['fas', 'recycle'],
  //   title: 'Release Guard',
  //   desc: 'Reset the rule',
  //   form: {
  //     type: '_release',
  //     options: {
  //       conditions: []
  //     }
  //   },
  // },
  [FlowGuardOperator.Suppress]: {
    name: FlowGuardOperator.Suppress,
    icon: ['fas', 'volume-xmark'],
    title: 'Suppression Guard',
    desc: 'Fire actions only once',
    form: {
      type: FlowGuardOperator.Suppress,
      options: {
        delay: 10,
        conditions: [{ _and: [] }]
      }
    },
    validate: async (form: GuardDelayForm) => {
      try {
        if (!(Number(form.options.delay) > 0)) throw "Delay must be a positive number"
        if (!hasAtLeastOneValidCondition(form?.options?.conditions || [])) {
          throw RULE_FORM_ERRORS.RULE_GUARD_DELAY_MISSING_CONDITIONS
        }
        return true
      } catch (error) {
        throw error
      }
    },
    displayComponent: defineAsyncComponent(() => import('@/models/rule/components/guards/RuleGuardDelay.vue')),
    formComponent: defineAsyncComponent(() => import('@/models/rule/components/guards/RuleGuardDelayForm.vue'))
  },
  [FlowGuardOperator.Cancel]: {
    name: FlowGuardOperator.Cancel,
    icon: ['fas', ICON.HOURGLASS_HALF],
    title: 'Delay & Cancel Guard',
    desc: 'Wait, then fire — unless something cancels it',
    form: {
      type: FlowGuardOperator.Cancel,
      options: {
        delay: 300,
        groupBy: 'personID',
        scope: 'entity',
        conditions: buildRuleFiredConditions('')
      }
    },
    validate: async (form: RuleGuardOf<CancelGuard>) => {
      if (!(Number(form.options.delay) > 0)) throw RULE_FORM_ERRORS.RULE_GUARD_CANCEL_INVALID_DELAY;
      if (!form.options.groupBy) throw RULE_FORM_ERRORS.RULE_GUARD_CANCEL_MISSING_GROUP_BY;
      if (!RULE_GUARD_CANCEL_SCOPE_OPTIONS.some((option) => option.value === form.options.scope)) {
        throw RULE_FORM_ERRORS.RULE_GUARD_CANCEL_INVALID_SCOPE;
      }
      const conditionsError = cancelConditionsError(form.options.conditions);
      if (conditionsError) throw conditionsError;
      return true;
    },
    displayComponent: defineAsyncComponent(
      () => import('@/models/rule/components/guards/rule-guard-cancel/RuleGuardCancel.vue')
    ),
    formComponent: defineAsyncComponent(
      () => import('@/models/rule/components/guards/rule-guard-cancel-form/RuleGuardCancelForm.vue')
    )
  }
};

type RuleGuardGeneric = {
  type: keyof GuardComponentMapType;
  options: Record<string, any>;
};

export const getRuleGuardIcon = (guard: RuleGuardGeneric): FAIcon => {
  switch (true) {
    case guard.type in guardComponentMap:
      return guardComponentMap[guard.type].icon;

    default:
      return ['fas', 'circle-question'];
  }
};

export const validateGuards = async (guards: RuleGuardGeneric[]): Promise<boolean> => {
  try {
    const hasInvalidGuards = await asyncSome<RuleGuardGeneric>(
      guards,
      async (guard): Promise<boolean> => {
        try {
          // if guard does not exist, assume user is in advance mode
          if (!guardComponentMap?.[guard.type]) {
            return true;
          }
          const { validate } = guardComponentMap[guard.type];
          const isValid = await validate(guard);
          return !isValid
        } catch (error) {
          throw error
        }
      }
    )
    return !hasInvalidGuards

  } catch (error) {
    throw error
  }
}

const RATE_LIMIT_CONFLICTS = new Set([FlowGuardOperator.Suppress, FlowGuardOperator.Cancel]);

const conflicts = (a: FlowGuardOperator, b: FlowGuardOperator) =>
  (a === FlowGuardOperator.RateLimit && RATE_LIMIT_CONFLICTS.has(b)) ||
  (b === FlowGuardOperator.RateLimit && RATE_LIMIT_CONFLICTS.has(a));

export const isGuardIncompatible = (type: FlowGuardOperator, selected: FlowGuardOperator[]) =>
  selected.some((other) => conflicts(type, other));

export const assertCompatibleGuards = ({ preGuards, postGuards }: Pick<Flow, 'preGuards' | 'postGuards'>) => {
  const types = [...(preGuards ?? []), ...(postGuards ?? [])].map(({ type }) => type);
  if (types.some((type) => isGuardIncompatible(type, types))) {
    throw RULE_FORM_ERRORS.RULE_GUARD_RATE_LIMIT_CONFLICT;
  }
};

export const hasUniqueGuards = (guards: RuleGuardGeneric[]): boolean => {
  try {
    /**
     * Returns an object with each key representing a
     * type of guard and its value representing the
     * number of times it is seen in the array.
     */
    const guardTypeCount = guards.reduce((acc, guard) => {
      if (!(guard.type in acc)) {
        acc[guard.type] = 0
      }
      acc[guard.type] += 1
      return acc
    }, {} as Record<string, number>)

    /**
     * Grabs all the values and filters it
     * down to see if there are any with
     * a value of more than 1.
     */
    const hasDuplicateGuards = Object.values(guardTypeCount)
      .filter((val: number) => val > 1)
      .length > 0;

    // Return true if there are no duplicates
    return !hasDuplicateGuards

  } catch (error) {
    throw error
  }
}
